import {Component, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {User} from '@models/user/user';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {TranslateService} from '@ngx-translate/core';
import {Role} from '@models/user/role';
import { HttpResponse } from '@angular/common/http';
import {UPDATE_STATE} from '@models/gateway/update/update-state.constant';
import {GATEWAY_TYPE} from '@constants/gateway/gateway-type';
import {SortEvent, TableHeaderSortableDirective} from '@shared/table-header-sortable/table-header-sortable.directive';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {GatewayService} from '@services/administration/gateway/gateway.service';
import {NotificationService} from '@services/notification/notification.service';
import {ArrayOperationService} from '@services/administration/array-operation/array-operation.service';
import {GatewayEditComponent} from '@components/administration/gateway/gateway-edit/gateway-edit.component';
import {Machine} from '@models/machine/machine';
import {GatewayUpdateComponent} from '@components/administration/gateway/gateway-update/gateway-update.component';
import {GATEWAY_STREAMING_TYPE} from '@constants/gateway/gateway-streaming-type';
import {GATEWAY_STATUS} from '@constants/gateway/gateway-status';
import {CONNECTION_STATE} from '@models/gateway/connection/connection-state.constant';
import {Organization} from '@models/organization/organization';
import {GatewayConfiguration} from '@models/adminstration/gateway/gateway-configuration';
import {UpdatableGateway} from '@models/adminstration/gateway/updatable-gateway';
import {GatewayIdList} from '@models/gateway/gateway-id-list';
import {MachineGroup} from '@models/adminstration/machine-group/machine-group';
import {StorageService} from '@services/storage/storage.service';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {ElasticSearchResult} from '@models/elastic-search/response/elastic-search-result';
import {ElasticSearchSortValue} from '@models/elastic-search/request/elastic-search-sort-value';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';
import {combineLatest, Observable, of, Subject, throwError, timer} from 'rxjs';
import {catchError, mergeMap, retry, startWith, takeUntil} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {ModalService} from '@services/modal/modal.service';
import {ITEMS_PER_PAGE_OPTIONS} from '@constants/pagination/pagination.constant';
import {PaginateControlSettings} from '@models/paginate-control-settings';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';

@Component({
    selector: 'scitis-admin-gateway',
    templateUrl: './gateway.component.html',
    styleUrls: ['./gateway.component.scss'],
    standalone: false
})
export class GatewayComponent implements OnInit, OnDestroy {

  @ViewChildren(TableHeaderSortableDirective) sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();

  public updateState: typeof UPDATE_STATE = UPDATE_STATE;
  public isDistributorRole: boolean = false;
  public isAdminRole: boolean = false;
  public isSuperAdminRole: boolean = false;
  public isTechAdminRole: boolean = false;
  public isLoading: boolean = false;
  public searchTerm: string = '';
  public itemsPerPageOptions: number[] = ITEMS_PER_PAGE_OPTIONS;
  public gatewayList: Gateway[] = new Array<Gateway>();
  public cloudplugLightList: Gateway[] = new Array<Gateway>();
  public machineList: Machine[] = new Array<Machine>();
  public updatableGateways: UpdatableGateway[] = new Array<UpdatableGateway>();

  public organizations: Organization[] = new Array<Organization>();
  public selectedOrganizationId: number | null = null;
  public selectedMachineryGroupId: number | null = null;
  public machineryGroupsList: MachineGroup[] = [];
  public gatewaysCount: number = 0;
  public paginateSettings: PaginateControlSettings = new PaginateControlSettings({
    id: 'gatewayPagination',
  });
  public gatewayStatusClasses: { [key in GATEWAY_STATUS]: string } = this.gatewayService.getGatewayStatusClasses();
  public updatedStateClassOptions: { [key in UPDATE_STATE]: string } = this.gatewayService.getUpdateStateClasses();

  private unfilteredGatewayList: Gateway[] = new Array<Gateway>();
  private CURRENT_PAGE_NAME: string = 'Gateways';
  private NON_CONTINUOUS_STREAMING_THRESHOLD_MINUTES: number = 5;
  private ORGANIZATION_AND_GROUP_INDEXED_DB_KEY: string = 'gatewaysOrganizationAndGroup';
  private firebaseUserId: string = '';
  private sortValue: ElasticSearchSortValue = new ElasticSearchSortValue();
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private searchSubject$: Subject<string> = new Subject<string>();
  private whiteListedMachineryGroups: null | number[] = null;
  private rootMachineryGroupId: number = -1;
  private rootOrganizationId: number = -1;
  private stopPolling$: Subject<any> = new Subject();
  private readonly POLLING_INTERVAL: number = 120_000;

  constructor(
      private loadingIndicator: LoadingIndicatorService,
      private translation: TranslateService,
      private modalService: ModalService,
      private firebaseAuthenticationService: FirebaseAuthenticationService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private gatewayService: GatewayService,
      private notificationService: NotificationService,
      private arrayOperationService: ArrayOperationService,
      private storageService: StorageService,
      private router: Router,
      private elasticSearchService: ElasticSearchService,
      private activatedRoute: ActivatedRoute,
      private sortingService: SortingService
  ) {
    this.activatedRoute.queryParams
    .pipe(takeUntil(this.destroy$))
    .subscribe(({search}) => {
      this.searchTerm = search || '';
      this.searchSubject$.next(this.searchTerm);
    });
  }

  public get appliedNumberOfFilters(): number {
    const filterArray: boolean[] = [];
    filterArray.push(!!this.selectedMachineryGroupId && this.selectedMachineryGroupId > 0);
    filterArray.push(!!this.selectedOrganizationId && this.selectedOrganizationId > 0);
    return filterArray.filter(v => v).length;
  }

  ngOnDestroy(): void {
    this.stopDataPolling();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.loadingIndicator.show(this.translation.instant('Loading'));
    this.initializeGatewayAdminPage();
    this.initPolling();
    this.searchSubject$
    .pipe(takeUntil(this.destroy$))
    .subscribe(value => {
      this.searchTerm = value;
      this.paginateSettings = {
        ...this.paginateSettings,
        currentPage: 1
      };
      this.loadGateways();
    });
  }

  public onSearchGateways({target}): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {search: target.value},
      queryParamsHandling: 'merge',
    });
  }

  onAddGateway() {
    const modalRef = this.modalService.open(GatewayEditComponent, {size: 'lg'});
    modalRef.componentInstance.gateway = new Gateway();
    modalRef.componentInstance.machines = this.machineList;
    modalRef.componentInstance.lightCloudplugs = this.cloudplugLightList;
    modalRef.componentInstance.rootOrganizationId = this.rootOrganizationId;
    modalRef.result.then((result) => {
      this.loadingIndicator.show(this.translation.instant('Adding gateway'));
      this.gatewayService.addGateway(result.sourceId, result.gateway).pipe(takeUntil(this.destroy$)).subscribe({
        next: (success) => {
          this.gatewaysCount++;
          this.paginateSettings = {
            ...this.paginateSettings,
            totalItems: this.gatewaysCount,
          };
          this.setGatewayUpdateStatus(success);
          success.configuration = this.determineConfigurationAvailability(success);
          success.usingSharedSecretKey = this.determineIfUsingAuthenticatedConnection(success);
          success.status = this.getGatewayStatusByType(success);
          this.gatewayList.push(success);

          const message = this.translation.instant('Gateway') + ' ' + success.name + ' ' + this.translation.instant('added') + '.';
          this.notificationService.success(message, this.CURRENT_PAGE_NAME, false);
          this.loadingIndicator.hide();

        }, error: (error) => {
          this.notificationService.error(this.translation.instant('Error adding gateway') + ': ' + result.name, error, this.CURRENT_PAGE_NAME, true);
          this.loadingIndicator.hide();
        }
      });
    });
  }

  onUpdateGateway() {
    this.setUpdatableGateways();
    const modalRef = this.modalService.open(GatewayUpdateComponent, {size: 'lg'});
    modalRef.componentInstance.updatableGateways = this.updatableGateways;
    modalRef.result.then((result) => {
      console.log(`onUpdateGateway Closed with: ${result}`);
      const gatewayIdList = new GatewayIdList();
      gatewayIdList.gatewayIds = result;
      this.loadingIndicator.show(this.translation.instant('Update of Gateways started'));
      this.gatewayService.updateGateways(result).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          const message = this.translation.instant('Updated Gateways successfully.');
          this.notificationService.success(message, this.CURRENT_PAGE_NAME, false);
          this.loadingIndicator.hide();
        }, error: (error) => {
          this.notificationService.error(this.translation.instant('Error updating gateways!') + ': ' + result.name, error, this.CURRENT_PAGE_NAME, true);
          this.loadingIndicator.hide();
        }
      });
    });
  }

  onDisplayUpdateAllGatewaysButton() {
    if (this.isSuperAdminRole || this.isTechAdminRole) {
      return this.isGatewayUpdatable();
    }
    return false;
  }

  public onColumnSort({column, direction}: SortEvent): void {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.sortValue = this.tableHeaderSortableService.getSortValue({column, direction});
    this.loadGateways();
  }

  public onItemsPerPageChange(itemsPerPage: number): void {
    this.paginateSettings = {
      ...this.paginateSettings,
      itemsPerPage: itemsPerPage,
      currentPage: 1,
    };
    this.loadGateways();
  }

  public onLoadPageNumber(pageNumber: number): void {
    this.paginateSettings = {
      ...this.paginateSettings,
      currentPage: pageNumber,
    };
    this.loadGateways();
  }

  onDeleteGatewaySuccess(gateway: Gateway): void {
    this.gatewaysCount--;
    this.paginateSettings = {
      ...this.paginateSettings,
      totalItems: this.gatewaysCount,
    };
    this.arrayOperationService.delete(gateway, this.gatewayList, this.unfilteredGatewayList);
  }

  onGatewayUpdated(gateway: Gateway): void {
    this.arrayOperationService.update(gateway, this.gatewayList, this.unfilteredGatewayList);
    window.location.reload();
  }

  public onChangeOrganization(organization: Organization) {
    if (organization) {
      this.selectedOrganizationId = organization.id;
    } else {
      this.selectedOrganizationId = null;
    }
  }

  public onChangeMachineryGroup(machineryGroup: MachineGroup) {
    if (machineryGroup) {
      this.selectedMachineryGroupId = machineryGroup.id;
    } else {
      this.selectedMachineryGroupId = null;
    }
  }

  onApplyFilterClick() {
    this.storageService.writeValueToIndexedDb(this.ORGANIZATION_AND_GROUP_INDEXED_DB_KEY + this.firebaseUserId, this.getOrganizationAndGroup());
    this.loadGateways();
  }

  private initPolling(): void {
    this.startPolling().subscribe(
        {
          next: (success: ElasticSearchResponseEntity) => {
            this.handleGettingGateways(success);
          },
          error: (error) => {
            console.error(`${this.CURRENT_PAGE_NAME}. Polling mechanism has been failed`, JSON.stringify(error));
          }
        }
    );
  }

  private initializeGatewayAdminPage(): void {
    this.firebaseAuthenticationService.getLoggedUserDetails()
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (user: User) => {
        this.firebaseUserId = user.firebaseUid;
        const userRole: Role = new Role(user);
        this.isSuperAdminRole = userRole.isSuperAdminRole;
        this.rootMachineryGroupId = userRole.isSuperAdminRole ? -1 : user.machineGroup;
        this.rootOrganizationId = userRole.isSuperAdminRole ? -1 : user.parentId;
        this.selectedOrganizationId = this.isSuperAdminRole ? null : user.parentId;
        this.selectedMachineryGroupId = this.isSuperAdminRole ? null : user.machineGroup;
        this.whiteListedMachineryGroups = userRole.isSuperAdminRole ? null : user.getWhiteListedMachineryGroups(true);
        this.isAdminRole = userRole.isAdminRole;
        this.isDistributorRole = userRole.isDistributorRole;
        this.isTechAdminRole = userRole.isTechAdminRole;

        this.setAllOrganizationsForUser();
        this.setAllMachineryGroupsForUser();
        this.setMachines();
        this.setLightCloudplugs();

        this.storageService.writeValueToIndexedDb(this.ORGANIZATION_AND_GROUP_INDEXED_DB_KEY + this.firebaseUserId, this.getOrganizationAndGroup());

      }, error: (error: HttpResponse<any>) => {
        console.error(`${this.CURRENT_PAGE_NAME}. Error getting current user details.`, JSON.stringify(error));
      }
    });
  }

  private getGateways() {
    this.setGatewaysCountForUser();
    return this.elasticSearchService.listGateways(this.searchTerm, this.sortValue, this.paginateSettings.currentPage,
        this.paginateSettings.itemsPerPage, this.selectedOrganizationId, this.selectedMachineryGroupId, this.whiteListedMachineryGroups, this.isSuperAdminRole)
    .pipe(takeUntil(this.destroy$), catchError((error) => {
      const message = this.translation.instant('Error getting gateway list.');
      this.isLoading = false;
      this.loadingIndicator.hide();
      this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
      return throwError(error);
    }));
  }

  private startPolling(): Observable<any> {
    return timer(this.POLLING_INTERVAL, this.POLLING_INTERVAL).pipe(
        startWith(1),
        mergeMap(() => this.getGateways()),
        retry(3),
        takeUntil(this.stopPolling$)
    );
  }

  private stopDataPolling() {
    this.stopPolling$.next('');
    this.stopPolling$.complete();
  }

  private handleGettingGateways(success: ElasticSearchResponseEntity) {
    if (success?.result?.data) {
      const result: ElasticSearchResult = success.result;
      for (const gateway of result.data) {
        this.setGatewayUpdateStatus(gateway);
        gateway.usingSharedSecretKey = this.determineIfUsingAuthenticatedConnection(gateway);
        gateway.status = this.getGatewayStatusByType(gateway);
        gateway.configuration = this.determineConfigurationAvailability(gateway);
      }
      this.unfilteredGatewayList = result.data;
      this.gatewayList = result.data;
      if (this.searchTerm) {
        this.gatewaysCount = result?.totalHits || 0;
        this.paginateSettings = {...this.paginateSettings, totalItems: this.gatewaysCount};
      } else {
        this.setGatewaysCountForUser().pipe(takeUntil(this.destroy$)).subscribe(res => {
          this.gatewaysCount = res?.result?.totalHits || 0;
          this.paginateSettings = {...this.paginateSettings, totalItems: this.gatewaysCount};
        });
      }
    }
    this.loadingIndicator.hide();
    this.isLoading = false;
  }

  private getOrganizationAndGroup() {
    return {
      organization: this.selectedOrganizationId,
      group: this.selectedMachineryGroupId
    };

  }

  private isGatewayUpdatable() {
    for (const gateway of this.gatewayList) {
      if (gateway.connectionStatus && gateway.connectionStatus.state === 'ONLINE' && gateway.updateStatus && gateway.updateStatus.state === UPDATE_STATE.AVAILABLE && this.isCloudplugOneOrEdge(gateway)) {
        return true;
      }
    }
    return false;
  }

  private isCloudplugOneOrEdge(gateway: Gateway) {
    return gateway.cloudplugType === GATEWAY_TYPE.CPONE || gateway.cloudplugType === GATEWAY_TYPE.CPEDGE;
  }

  private loadGateways() {
    if (this.elasticSearchService.isInvalidSearchTerm(this.searchTerm)) {
      this.unfilteredGatewayList = [];
      this.gatewayList = [];
      this.gatewaysCount = 0;
      this.paginateSettings = {...this.paginateSettings, totalItems: this.gatewaysCount};
      return;
    }

    this.loadingIndicator.show(this.translation.instant('Loading'));
    const listObs = this.elasticSearchService
    .listGateways(
        this.searchTerm,
        this.sortValue,
        this.paginateSettings.currentPage,
        this.paginateSettings.itemsPerPage,
        this.selectedOrganizationId,
        this.selectedMachineryGroupId,
        this.whiteListedMachineryGroups,
        this.isSuperAdminRole
    ).pipe(catchError(error => {
      const message = this.translation.instant('Error getting gateway list.');
      this.isLoading = false;
      this.loadingIndicator.hide();
      this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
      return of(null);
    }), startWith(null));
    const countObserver = this.searchTerm ? of(null) : this.setGatewaysCountForUser();

    combineLatest([listObs, countObserver])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([res, count]) => {
      if (res) {
        this.handleGettingGateways(res);
      }
      if (count) {
        this.gatewaysCount = count?.result?.totalHits || 0;
        this.paginateSettings = {...this.paginateSettings, totalItems: this.gatewaysCount};
      }
    });
  }

  private setGatewayUpdateStatus(gateway: Gateway): void {
    if (!gateway.updateStatus || !gateway.updateStatus.state) {
      gateway.updateStatus = {
        lastUpdate: new Date(), nextUpdate: new Date(), state: UPDATE_STATE.NONE
      };
    }
    const updateState = gateway.updateStatus.state;
    switch (updateState) {
      case 'SUCCESS':
        gateway.updateStatus.state = UPDATE_STATE.SUCCESS;
        break;
      case 'AVAILABLE':
        gateway.updateStatus.state = UPDATE_STATE.AVAILABLE;
        break;
      case 'PROGRESS':
        gateway.updateStatus.state = UPDATE_STATE.PROGRESS;
        break;
      case 'PENDING':
        gateway.updateStatus.state = UPDATE_STATE.PENDING;
        break;
      case 'ERROR':
        gateway.updateStatus.state = UPDATE_STATE.ERROR;
        break;
      default:
        gateway.updateStatus.state = UPDATE_STATE.NONE;
        break;
    }
  }

  private determineConfigurationAvailability(gateway: any) {
    const gatewayConfiguration: GatewayConfiguration = new GatewayConfiguration();

    if (gateway.cloudplugType === GATEWAY_TYPE.CPONE) {
      if (gateway.configPath && gateway.configPath.length > 0) {
        gatewayConfiguration.status = this.translation.instant('available');
        gatewayConfiguration.cssClass = 'badge-success';
      } else {
        gatewayConfiguration.status = this.translation.instant('missing');
        gatewayConfiguration.cssClass = 'badge-danger';
      }
      gatewayConfiguration.lastUpload = gateway.configLastUploadTimestamp;
    } else {
      gatewayConfiguration.status = this.translation.instant('unsupported');
      gatewayConfiguration.cssClass = 'badge-info';
    }
    return gatewayConfiguration;
  }

  private setMachines() {
    this.elasticSearchService.listAllMachines(this.rootOrganizationId, this.rootMachineryGroupId, this.whiteListedMachineryGroups)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (success: ElasticSearchResponseEntity) => {
        if (success?.result?.data && success.result.data.length > 0) {
          const result: ElasticSearchResult = success.result;
          this.machineList = result.data;
        }
      }, error: (error) => {
        console.log(error);
      }
    });
  }

  private setLightCloudplugs() {
    this.cloudplugLightList = [];
    this.elasticSearchService.listAllGatewaysByType(null, null, null, true, GATEWAY_TYPE.CPLIGHT).pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (response: ElasticSearchResponseEntity) => {
        if (response?.result?.data?.length > 0) {
          this.cloudplugLightList = [...response.result.data];
        }

      }, error: (error) => {
        console.log(error);
      }
    });
  }

  private setUpdatableGateways() {
    this.updatableGateways = [];
    for (const gateway of this.gatewayList) {
      if (gateway.connectionStatus.state === 'ONLINE' && gateway.updateStatus && gateway.updateStatus.state === UPDATE_STATE.AVAILABLE && this.isCloudplugOneOrEdge(gateway)) {
        const updatableGateway: UpdatableGateway = {
          id: gateway.id,
          name: gateway.name,
          machineName: gateway.machineName,
          tenantName: gateway.tenantName,
          selected: true
        };
        this.updatableGateways.push(updatableGateway);
      }
    }
  }

  private determineIfUsingAuthenticatedConnection(gateway: any) {
    if (gateway.cloudplugType === GATEWAY_TYPE.CPLIGHT || gateway.cloudplugType === GATEWAY_TYPE.ARGOS_GATEWAY) {
      return true;
    }
    return gateway.sharedSecretKey;
  }

  private getGatewayStatusByType(gateway: any): GATEWAY_STATUS {
    if (gateway.cloudplugType === GATEWAY_TYPE.CPONE) {
      const streamingType: GATEWAY_STREAMING_TYPE = this.getGatewayStreamingType(gateway);
      if (streamingType === GATEWAY_STREAMING_TYPE.NON_CONTINUOUS) {
        return this.determineStateNonContinuousStreaming(gateway);
      } else if (streamingType === GATEWAY_STREAMING_TYPE.CONTINUOUS) {
        return this.determineStateContinuousStreaming(gateway);
      } else {
        return GATEWAY_STATUS.OFFLINE_OR_UNKNOWN;
      }
    } else {
      return this.determineStateContinuousStreaming(gateway);
    }
  }

  private getGatewayStreamingType(gateway: any): GATEWAY_STREAMING_TYPE {
    if (!gateway.streamingDetails || !gateway.streamingDetails.streamingType) {
      return GATEWAY_STREAMING_TYPE.UNKNOWN;
    }
    for (const streamingType in GATEWAY_STREAMING_TYPE) {
      if (streamingType === gateway.streamingDetails.streamingType.toUpperCase()) {
        return GATEWAY_STREAMING_TYPE[streamingType];
      }
    }
    return GATEWAY_STREAMING_TYPE.UNKNOWN;
  }

  private determineStateNonContinuousStreaming(gateway: any): GATEWAY_STATUS {
    if (!gateway.streamingDetails || !gateway.streamingDetails.streamingType || !gateway.streamingDetails.lastUpdateRequestTimestamp) {
      return GATEWAY_STATUS.MISSING;
    }
    const lastUpdateRequestTimestamp = gateway.streamingDetails.lastUpdateRequestTimestamp;
    const now = new Date().getTime();
    const offsetReference = now - this.NON_CONTINUOUS_STREAMING_THRESHOLD_MINUTES * 60 * 1000;
    if (lastUpdateRequestTimestamp < offsetReference) {
      return GATEWAY_STATUS.OFFLINE_OR_UNKNOWN;
    } else {
      return GATEWAY_STATUS.ONLINE;
    }
  }

  private determineStateContinuousStreaming(gateway: any): GATEWAY_STATUS {
    if (!gateway.connectionStatus) {
      return GATEWAY_STATUS.OFFLINE_OR_UNKNOWN;
    }

    if (gateway.connectionStatus.state === CONNECTION_STATE.ONLINE && gateway.sendingData) {
      return GATEWAY_STATUS.ONLINE;
    } else if (gateway.connectionStatus.state === CONNECTION_STATE.ONLINE && !gateway.sendingData) {
      return GATEWAY_STATUS.PARTIALLY_DISCONNECTED;
    } else if (gateway.connectionStatus.state === CONNECTION_STATE.OFFLINE || gateway.connectionStatus.state === CONNECTION_STATE.UNKNOWN) {
      return GATEWAY_STATUS.OFFLINE_OR_UNKNOWN;
    } else {
      return GATEWAY_STATUS.MISSING;
    }
  }

  private setAllOrganizationsForUser(): void {
    this.elasticSearchService.listAllOrganizations(this.rootOrganizationId)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (success: ElasticSearchResponseEntity) => {
        if (success?.result?.data && success.result.data.length > 0) {
          this.organizations = this.sortingService.sortArrayByProperty(success.result.data, 'description', true);
        }
      }, error: (error: HttpResponse<any>) => {
        console.error(`${this.CURRENT_PAGE_NAME}. Error getting organizations list.`, JSON.stringify(error));
      }
    });
  }

  private setAllMachineryGroupsForUser(): void {
    this.loadingIndicator.show();
    this.elasticSearchService.listAllMachineryGroups(this.rootMachineryGroupId, this.whiteListedMachineryGroups)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (success: ElasticSearchResponseEntity) => {
        if (success?.result?.data && success.result.data.length > 0) {
          this.machineryGroupsList = success.result.data;
          if (!this.isSuperAdminRole && this.whiteListedMachineryGroups && this.whiteListedMachineryGroups.length > 0) {
            this.selectedMachineryGroupId = this.whiteListedMachineryGroups[0];
          }
        }
        this.loadingIndicator.hide();
      }, error: _error => {
        console.error(`${this.CURRENT_PAGE_NAME}. Error getting machinery groups list.`, JSON.stringify(_error));
        this.loadingIndicator.hide();
      }
    });
  }

  private setGatewaysCountForUser(): Observable<any> {
    return this.elasticSearchService.countGateways(this.selectedOrganizationId, this.selectedMachineryGroupId, this.whiteListedMachineryGroups, this.isSuperAdminRole)
    .pipe(catchError(error => {
      this.gatewaysCount = 0;
      console.error(`${this.CURRENT_PAGE_NAME}. Error getting gateways count.`, JSON.stringify(error));
      return of(null);
    }), startWith(null));
  }
}
